import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useBrands } from '../contexts/BrandContext'
import { Icons } from '../components/icons'
import { can } from '../utils/permissions'
import { avatarGradient, brandColor, brandInitial } from '../utils/brand'
import { initialsOf } from '../utils/text'
import { loadVcards } from '../services/vcardStore'
import type { Vcard } from '../types'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select'

/** Listen-Seite der vCards — admin (alle) & brand-admin (eigene Brand), Spec §5.6/§7.4.
 *  BMC-Designsystem `console.css`. */
export function VcardListPage() {
  const { session } = useAuth()
  const { brands, resolveBrand } = useBrands()
  const navigate = useNavigate()
  const location = useLocation()
  const [vcards, setVcards] = useState<Vcard[] | null>(null)
  const [brandFilter, setBrandFilter] = useState<string>('all')

  // Herkunftspfad (inkl. evtl. Query) für den Rücksprung aus dem Editor merken.
  const listPath = `${location.pathname}${location.search}`

  useEffect(() => {
    if (!session) return
    let cancelled = false
    loadVcards(session).then((list) => {
      if (!cancelled) setVcards(list)
    })
    return () => {
      cancelled = true
    }
  }, [session])

  if (!session) return null

  const gate = can(session)
  const title = session.role === 'admin' ? 'Alle vCards' : 'vCards deiner Brand'

  const filteredVcards =
    vcards === null ? null : vcards.filter((vc) => brandFilter === 'all' || vc.brandKey === brandFilter)

  return (
    <>
      <section className="cover">
        <div className="eyebrow">{session.role === 'admin' ? 'Administration' : 'Deine Brand'}</div>
        <h1>
          Alle <span className="hl">vCards</span> verwalten
        </h1>
        <p>Verwalte hier die vCards im eigenen Verantwortungsbereich.</p>
      </section>

      <section className="panel">
        <div className="head">
          <h2>{title}</h2>
          {filteredVcards && <span className="sub">{filteredVcards.length} Einträge</span>}
          <span className="spacer" />
          <Select value={brandFilter} onValueChange={setBrandFilter}>
            <SelectTrigger id="vcard-brand-filter" aria-label="Brand" className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Brands</SelectItem>
              {brands.map((b) => (
                <SelectItem key={b.brandKey} value={b.brandKey}>
                  {b.brandName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {gate.vcardCreate() && (
            <button type="button" className="btn primary sm" onClick={() => navigate('/editor/vcards/new')}>
              {Icons.plus} Neue vCard
            </button>
          )}
        </div>

        <div className="list">
          {filteredVcards === null ? (
            <div className="empty">vCards werden geladen …</div>
          ) : filteredVcards.length === 0 ? (
            <div className="empty">Keine vCards in deinem Bereich.</div>
          ) : (
            filteredVcards.map((vc) => {
              const b = resolveBrand(vc.brandKey)
              const name = vc.firstName || vc.lastName ? `${vc.firstName ?? ''} ${vc.lastName ?? ''}`.trim() : 'Neue vCard'
              const canEdit = gate.vcardEdit(vc)
              return (
                <div className="lrow" key={vc.urlKey}>
                  {vc.profileImageUrl ? (
                    <img className="pic" src={vc.profileImageUrl} alt="" />
                  ) : (
                    <span className="pic" style={{ background: avatarGradient(vc.urlKey) }}>
                      {initialsOf(vc.firstName, vc.lastName)}
                    </span>
                  )}
                  <div className="who">
                    <div className="nm">{name}</div>
                    <div className="mt">
                      {vc.role || '—'} · <span className="path">/{vc.urlKey}</span>
                    </div>
                  </div>
                  <div className="side">
                    {session.role === 'admin' && (
                      <span className="chip-brand" style={{ background: brandColor(b) }} title={b.brandName}>
                        {brandInitial(b)}
                      </span>
                    )}
                    <span className={`badge ${vc.active ? 'active' : 'inactive'}`}>
                      {vc.active && <span className="dot" />}
                      {vc.active ? 'aktiv' : 'inaktiv'}
                    </span>
                    <a className="btn ghost sm" href={vc.publicUrl ?? `/${vc.urlKey}`} target="_blank" rel="noreferrer">
                      {Icons.eye} Ansehen
                    </a>
                    {canEdit ? (
                      <button
                        type="button"
                        className="btn outline sm"
                        onClick={() => navigate(`/editor/vcards/${vc.urlKey}`, { state: { from: listPath } })}
                      >
                        {Icons.edit} Bearbeiten
                      </button>
                    ) : (
                      <button type="button" className="btn ghost sm" disabled title="Keine Berechtigung">
                        {Icons.lock} Gesperrt
                      </button>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </section>
    </>
  )
}
